import FolderIconDark from '@Resources/assets/icons/dark/folder.svg';
import FolderIconDarkGreen from '@Resources/assets/icons/dark/folderGreen.svg';
import FolderIconGreyGreen from '@Resources/assets/icons/dark/folderGreyGreen.svg';
import FolderIconGrey from '@Resources/assets/icons/dark/folderSmGrey.svg';
import NotVisibleIconGrey from '@Resources/assets/icons/dark/notVisibleGrey.svg';
import SelectFileIconDark from '@Resources/assets/icons/dark/selectFile.svg';
import VisibleIconDark from '@Resources/assets/icons/dark/visible.svg';
import FolderIconLightGreen from '@Resources/assets/icons/light/folderGreen.svg';
import FolderIconLight from '@Resources/assets/icons/light/folderSm.svg';
import VisibleIconLight from '@Resources/assets/icons/light/visible.svg';
import ColorsStyles from '@Styles/colors.scss';
import Flex from '@Styles/flex.scss';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    AccordionSummaryProps,
    styled,
} from '@mui/material';
import { GridApi, GridAutosizeOptions, GridColDef } from '@mui/x-data-grid';
import cx from 'classnames';
import _isNil from 'lodash/isNil';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import FoldersApi from '@Data/folders/Api';
import { IFolder } from '@Data/folders/Interfaces';
import {
    expandedFolderIdsState,
    selectedFolderIdsState,
    visibleFoldersState,
} from '@Data/folders/Reducer';
import { IProcess } from '@Data/processes/Interfaces';
import Styles from './Folders.scss';
import QcSnapshotFolderOption from './QcSnapshotFolderOption';

interface IFolderOptionProps {
    id?: string;
    folder: IFolder;
    linkDepartmentProcess?: IProcess;
    onClick: () => void;
    folderMenu: any;
    containerCss?: string;
    gridRef: React.MutableRefObject<GridApi>;
    columns: GridColDef[];
    setColumns: (GridColDef) => void;
    isInProgress?: boolean;
}

interface IFolderOptionAccordionProps extends AccordionSummaryProps {
    isSelected: boolean;
    isVisible: boolean;
    isLinked: boolean;
}

const getBackgroundColor = (
    isSelected: boolean,
    isHovering = false
): string => {
    if (isSelected) {
        return ColorsStyles.ikeGradient1;
    } else {
        return isHovering ? ColorsStyles.ikeGradient12 : '#fff';
    }
};

const getFontColor = (isSelected: boolean, isVisible: boolean): string => {
    if (isSelected) {
        return ColorsStyles.ikeGradient8;
    } else {
        return ColorsStyles.ikeGradient8;
        return isVisible
            ? ColorsStyles.ikeGradient1
            : ColorsStyles.ikeGradient8;
    }
};

const IO_IMPORT_V2 = 'io_import_v2';

const getFolderIcon = (
    isLinked: boolean,
    isSelected: boolean,
    isVisible: boolean,
    hasSavedData: boolean
) => {
    if (hasSavedData) {
        return <SelectFileIconDark />;
    }
    if (isSelected) {
        return isLinked ? <FolderIconLightGreen /> : <FolderIconLight />;
    } else if (isLinked) {
        return isVisible ? <FolderIconDarkGreen /> : <FolderIconGreyGreen />;
    } else {
        return isVisible ? <FolderIconDark /> : <FolderIconGrey />;
    }
};

const getFolderFontColor = (
    isLinked: boolean,
    isSelected: boolean,
    isVisible: boolean
): string => {
    return isLinked ? '#fff' : getFontColor(isSelected, isVisible);
};

const getVisibilityIcon = (isSelected: boolean, isVisible: boolean) => {
    if (isSelected) {
        return <VisibleIconLight />;
    } else {
        return isVisible ? <VisibleIconDark /> : <NotVisibleIconGrey />;
    }
};

const StyledAccordionSummary = styled(AccordionSummary, {
    shouldForwardProp: (p) =>
        p !== 'isSelected' && p !== 'isVisible' && p !== 'isLinked',
})<IFolderOptionAccordionProps>(
    ({ isSelected, isVisible, isLinked }) => `



    align-items: center;
    letter-spacing: 0.02em;
    flex-direction: row-reverse;
    gap: 8px;
    margin: 0px;
    padding: 0px;
    min-height: 24px;

    .MuiAccordionSummary-content {
        margin: 0px;
        padding: 0px;
    },

    .${Styles.foTitle} {
        color: ${getFontColor(isSelected, isVisible)};
    }

    .${Styles.folderNumPoles} {
        color: ${getFolderFontColor(isLinked, isSelected, isVisible)}
    }   


    .${Styles.foVisibilityContainer}, .${Styles.foMenuContainer} {
        display: flex;
    }

`
);

export const FolderOption = (props: IFolderOptionProps) => {
    const {
        id,
        folder,
        onClick,
        folderMenu,
        containerCss,
        gridRef,
        columns,
        setColumns,
        isInProgress,
    } = props;

    const qcFolders = folder.child_folders;
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const isLinked = folder.folder_type === IO_IMPORT_V2;

    const handleToggleIsExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    const visibleFolders: Array<IFolder> = useSelector(visibleFoldersState);
    const selectedFolderIds: Array<string> = useSelector(
        selectedFolderIdsState
    );
    const expandedIds: Array<string> = useSelector(expandedFolderIdsState);

    useMemo(() => {
        if (selectedFolderIds.includes(folder.folder_id)) {
            setIsSelected(true);
        }
        if (visibleFolders.map((f) => f.folder_id).includes(folder.folder_id)) {
            setIsVisible(true);
        }
        if (expandedIds.includes(folder.folder_id)) {
            setIsExpanded(true);
        }
    }, [folder, visibleFolders, selectedFolderIds, expandedIds]);

    const folderIcon = getFolderIcon(
        isLinked,
        isSelected,
        isVisible,
        isInProgress
    );
    const visibilityIcon = getVisibilityIcon(isSelected, isVisible);

    const expandIcon =
        !_isNil(qcFolders) &&
        Array.isArray(qcFolders) &&
        qcFolders.length > 0 ? (
            <ExpandMoreIcon
                onClick={handleToggleIsExpanded}
                style={{
                    color: getFontColor(isSelected, isVisible),
                    fontSize: '1rem',
                }}
            />
        ) : (
            <div style={{ width: '1rem' }} />
        );

    const handleViewQcRun = (qcFolder) => {
        console.log(qcFolder);
    };

    return (
        <Accordion
            id={`fo-accordion-${folder.folder_id}`}
            expanded={isExpanded}
            square={true}
            disableGutters={true}
            style={{
                margin: '0',
                padding: '0',
                backgroundColor: 'inherit',
                overflow: 'visible',
                width: 'max-content',
            }}
            onChange={(event, expanded) => {
                let gridsize: GridAutosizeOptions = {
                    columns: ['folder'],
                    expand: expanded,
                };
                if (expanded && columns && columns.length > 0) {
                    columns.find((col) => col.field === 'folder').width = 499;
                } else {
                    columns.find((col) => col.field === 'folder').width = 101;
                }

                if (expanded) {
                    FoldersApi.setExpandedFolderIds(
                        expandedIds.concat(folder.folder_id)
                    );
                } else {
                    let updateArray = expandedIds.filter(
                        (id) => id !== folder.folder_id
                    );
                    FoldersApi.setExpandedFolderIds(updateArray);
                }

                setColumns([...columns]);
            }}
        >
            <StyledAccordionSummary
                isSelected={isSelected}
                isVisible={isVisible}
                isLinked={isLinked}
                onClick={(e) => e.stopPropagation()}
                expandIcon={null}
            >
                <div
                    id={`${folder.folder_id}-container`}
                    className={cx(
                        Flex.flex,
                        Flex.justifyBetween,
                        containerCss,
                        Styles.fullFolderContainer
                    )}
                >
                    <div className={Styles.foRightContainer}>
                        {!_isNil(folderMenu) && <>{folderMenu}</>}
                    </div>
                    <div className={Styles.folderOptionContainer}>
                        <div
                            className={Styles.foMainContainer}
                            onClick={(e) => {
                                onClick();
                            }}
                        >
                            {expandIcon}
                            {folderIcon}
                        </div>
                    </div>
                </div>
            </StyledAccordionSummary>
            <AccordionDetails id="entity-data-accordion-details">
                {qcFolders.map((qcFolder) => {
                    return (
                        <QcSnapshotFolderOption
                            key={qcFolder.folder_id}
                            qcFolderId={qcFolder.folder_id}
                            name={qcFolder.folder_name}
                            numberPoles={qcFolder.pole_number}
                            onClick={() => handleViewQcRun(qcFolder)}
                        />
                    );
                })}
            </AccordionDetails>
        </Accordion>
    );
};
